import { runInAction } from 'mobx';
import Cookies from 'js-cookie';
import { sha3_512 } from 'js-sha3';
import { isFlagSet, getExperienceVariant } from '../featureFlags/featureFlags';
import { FEATURE_FLAGS } from '../featureFlags/constants';
import { GUEST_WISH_LIST_EXPERIENCE } from '../../stores/constants';

export const mapProductWishLists = (
    wishLists: ProductWishList[],
    skuId: string,
    productMetadata?: ProductMetadata,
): ProductWishList[] => {
    wishLists.forEach((wishList, index) => {
        const matchedProducts = wishList.products.filter((product) => product.skuId === skuId);
        if (matchedProducts.length > 0) {
            if (productMetadata) {
                wishLists[index].isAdded = matchedProducts.some((matchedProduct) =>
                    matchedProductMetadata(matchedProduct, productMetadata),
                );
            } else {
                wishLists[index].isAdded = true;
            }
        } else {
            wishLists[index].isAdded = false;
        }
    });

    return wishLists;
};

export const isItemInWishList = (wishLists: ProductWishList[], wishListId?: string): boolean => {
    return wishLists.find((wishListItem) => wishListItem.id === wishListId)?.isAdded ?? false;
};

export const mapWishLists = (wishLists: WishListItem[]): ProductWishList[] => {
    return wishLists.map((wishList) => ({
        id: wishList.id,
        name: wishList.name,
        isAdded: false, // handled by mapProductWishLists
        isFull: wishList.full,
        products: wishList.products,
        shared: wishList.shared,
        image: wishList.image,
    }));
};

export const updateProductWishList = (
    productWishlists: ProductWishList[],
    updatedProductWishList: ProductWishList,
): ProductWishList[] => {
    const index = productWishlists.findIndex((productWishList) => productWishList.id == updatedProductWishList.id);
    if (index >= 0) {
        runInAction(() => {
            productWishlists[index] = updatedProductWishList;
        });
    }

    return productWishlists;
};

export const buildURL = (path: string): string => {
    let newPath = path;
    if (newPath.includes('?')) {
        newPath += '&';
    } else {
        newPath += '?';
    }

    newPath += 'asaf=true';

    if (newPath[0] != '/') {
        newPath = '/' + newPath;
    }

    return `${process.env.REACT_APP_API_HOST}/v1${newPath}`;
};

export const buildProductMetadataQuery = (productMetadata: ProductMetadata | undefined): string => {
    if (!productMetadata) {
        return '';
    }

    let query = '';

    if (productMetadata.asafProperties) {
        const fabricSku = productMetadata?.asafProperties?.fabricSku;
        const legType = productMetadata?.asafProperties?.legType;

        const fabricQuery = fabricSku ? `&fabricSku=${fabricSku}` : '';
        const legQuery = legType ? `&legType=${legType}` : '';
        query = fabricQuery + legQuery;
    }

    return query;
};

export const matchedProductMetadata = (product: Product, productMetadata: ProductMetadata): boolean => {
    if (productMetadata.asafProperties) {
        return (
            (product.asafProperties?.fabricSku == productMetadata.asafProperties.fabricSku &&
                product.asafProperties?.legType == productMetadata.asafProperties.legType) ||
            (product.asafProperties?.fabricSku == productMetadata.asafProperties.fabricSku &&
                !product.asafProperties?.legType)
        );
    }

    return true;
};

export const removeQueryParamFromURL = (url: string, queryParam: string): void => {
    let queryParamToReplace = queryParam;

    if (url.includes(`?${queryParamToReplace}`)) {
        queryParamToReplace = `?${queryParamToReplace}`;
    } else if (url.includes(`&${queryParamToReplace}`)) {
        queryParamToReplace = `&${queryParamToReplace}`;
    }

    const updatedURL = url.replace(queryParamToReplace, '');
    history.pushState(null, '', updatedURL);
};

const logToNewrelic = (eventName: string, details?: object): void => {
    const shopperId = Cookies.get('shopperId')?.toLocaleUpperCase();
    const basketId = Cookies.get('basket_id')?.toLocaleLowerCase();
    const shopperIdSHA = shopperId ? sha3_512(shopperId) : 'GUEST_USER';
    const allDetails = {
        ...details,
        shopperId: shopperIdSHA,
        basketId: basketId,
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (window.newrelic?.addPageAction && typeof window.newrelic.addPageAction !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.newrelic.addPageAction(eventName, allDetails);
    } else {
        // Log it to console so that the errors are available in the ContentSquare sessions
        console.error(`${eventName} : ${JSON.stringify(allDetails)}`);
    }
};

export const logAPIErrorToNewrelic = (methodName: string, msg: string, details = {}): void => {
    const aggregatedDetails = {
        ...details,
        message: msg,
        methodName: methodName,
    };
    logToNewrelic('Add-To-WishList-API-Error', aggregatedDetails);
};

export const logAddItemDetailsToNR = (details = {}): void => {
    logToNewrelic('Add-To-WishList-item-details', details);
};

export const isGuestUser = (): boolean => {
    const hasShopperId = Cookies.get('shopperId');
    if (hasShopperId) return false;
    const guestABExpIsOn = isFlagSet(FEATURE_FLAGS.GUEST_WISHLIST);
    if (!guestABExpIsOn) return false;
    const variant = getExperienceVariant(GUEST_WISH_LIST_EXPERIENCE);
    return variant === 'A' || variant === 'Experiment' || variant === 'A-Experiment' || variant === 'A-ExperimentA';
};
